'use client'

import { useEffect, useRef } from 'react'
import Head from 'next/head'
import Image from 'next/image'
import { motion } from 'framer-motion'

export default function EnergyHousePage() {
  const scriptLoaded = useRef(false)

  // Load the Reonic script on component mount
  useEffect(() => {
    // Prevent multiple script loading
    if (scriptLoaded.current) return

    // Check if script already exists
    const existingScript = document.querySelector(
      'script[src="https://apps.reonic.de/elements/reonic-loader.js"]'
    )
    if (existingScript) {
      scriptLoaded.current = true
      return
    }

    const script = document.createElement('script')
    script.src = 'https://apps.reonic.de/elements/reonic-loader.js'
    script.defer = true
    script.type = 'text/javascript'
    script.onload = () => {
      scriptLoaded.current = true
      console.log('Reonic script loaded successfully')
    }
    script.onerror = (error) => {
      console.error('Error loading Reonic script:', error)
    }
    document.head.appendChild(script)

    return () => {
      // Only remove the script if we added it
      if (scriptLoaded.current) {
        const script = document.querySelector(
          'script[src="https://apps.reonic.de/elements/reonic-loader.js"]'
        )
        if (script) {
          document.head.removeChild(script)
        }
        scriptLoaded.current = false
      }
    }
  }, [])

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Content Section */}
      <div className="mx-auto py-40">
        {/* Reonic Widget Container */}
        <div className="mx-auto rounded-lg bg-gray-50 shadow-lg">
          <div
            data-reonic-type="element"
            data-product="energyhouse"
            data-user-id="4d4db258-69c5-4413-b0fd-0888aa8355bc"
            data-client-id="6b0119b2-19dc-4ed2-9a56-0959e055739a"
            data-iframe-height="true"
            id="reonic-energyhouse-container"
            className="min-h-[600px] w-full"
          ></div>
        </div>

        {/* Benefits Section */}
        <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-3">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="rounded-lg bg-white p-6 shadow-md dark:bg-gray-800"
          >
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#ff4500]">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <h3 className="mb-2 text-xl font-bold text-gray-900 dark:text-white">
              Energieeinsparungen visualisieren
            </h3>
            <p className="text-gray-700 dark:text-gray-300">
              Sehen Sie in Echtzeit, wie verschiedene Maßnahmen die Energieeffizienz Ihres Hauses
              verbessern können.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="rounded-lg bg-white p-6 shadow-md dark:bg-gray-800"
          >
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#ff4500]">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <h3 className="mb-2 text-xl font-bold text-gray-900 dark:text-white">
              Kostensimulation
            </h3>
            <p className="text-gray-700 dark:text-gray-300">
              Kalkulieren Sie potenzielle Kosteneinsparungen durch verschiedene
              Energieeffizienzmaßnahmen.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="rounded-lg bg-white p-6 shadow-md dark:bg-gray-800"
          >
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#ff4500]">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
            </div>
            <h3 className="mb-2 text-xl font-bold text-gray-900 dark:text-white">
              Individuelle Beratung
            </h3>
            <p className="text-gray-700 dark:text-gray-300">
              Erhalten Sie maßgeschneiderte Empfehlungen für die Optimierung der Energieeffizienz
              Ihres Zuhauses.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  )
}
